/**
 * Unified notification API.
 *
 * Thin wrapper around `react-hot-toast` that gives us four call shapes
 * (success / error / warning / info) with consistent icons, colors, and
 * the animation tuning configured globally on the `<Toaster>` in App.tsx.
 *
 * Usage:
 *   import { notify } from '@/utils/notify';
 *   notify.success('Contact saved');
 *   notify.error('Could not save', { description: 'Try again later.' });
 *   notify.warning('You have unsaved changes');
 *   notify.info('Auto-saved at 12:04');
 *
 * Why a wrapper at all instead of calling react-hot-toast directly:
 *   - the library only ships `success`, `error`, and a default toast;
 *     there's no first-class `warning`. We render that via `toast.custom`
 *     with a matching AlertTriangle icon + amber styling so it slots
 *     into the same visual family.
 *   - keeps callsites decoupled from the underlying lib so we can swap
 *     react-hot-toast for sonner / our own primitive without a sweep.
 *   - one place to tune defaults (duration, position, animation).
 */
import { ReactElement, ReactNode } from 'react';
import toast, { Toast, ToastPosition } from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { createElement } from 'react';

const DEFAULT_POSITION: ToastPosition = 'top-right';
const DEFAULT_DURATION_MS = 4000;
const ERROR_DURATION_MS = 6000; // errors stick around a little longer

interface NotifyOptions {
    /** Optional secondary line shown below the title for context. */
    description?: string;
    /** Auto-dismiss timeout (ms). Pass `Infinity` for a sticky toast. */
    duration?: number;
    /** Override position (defaults to top-right). */
    position?: ToastPosition;
    /** Optional stable id so the same toast can be replaced instead of stacked. */
    id?: string;
}

/**
 * Internal renderer used for every variant so the visual treatment
 * stays consistent. The body is rendered via `toast.custom` (rather
 * than relying on react-hot-toast's default success/error markup) so
 * the warning variant — which the library doesn't ship — slots in
 * seamlessly. Tailwind transitions handle the enter/exit animation.
 */
function makeBody(
    t: Toast,
    variant: 'success' | 'error' | 'warning' | 'info',
    title: string,
    description?: string,
): ReactElement {
    const palette: Record<typeof variant, { ring: string; icon: ReactNode; }> = {
        success: { ring: 'ring-emerald-200 bg-white', icon: createElement(CheckCircle2, { size: 18, className: 'text-emerald-500 shrink-0' }) },
        error:   { ring: 'ring-red-200 bg-white',     icon: createElement(XCircle,       { size: 18, className: 'text-red-500 shrink-0' }) },
        warning: { ring: 'ring-amber-200 bg-white',   icon: createElement(AlertTriangle, { size: 18, className: 'text-amber-500 shrink-0' }) },
        info:    { ring: 'ring-blue-200 bg-white',    icon: createElement(Info,          { size: 18, className: 'text-blue-500 shrink-0' }) },
    };
    const { ring, icon } = palette[variant];

    return createElement(
        'div',
        {
            className: [
                'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl px-4 py-3 shadow-lg ring-1',
                ring,
                // Smooth slide-down + fade. react-hot-toast toggles the
                // `data-state` enter/leave via the `t.visible` flag — we
                // translate that into Tailwind transitions for a buttery
                // animation that matches the rest of the app.
                'transform transition-all duration-300 ease-out',
                t.visible
                    ? 'translate-y-0 opacity-100 scale-100'
                    : '-translate-y-2 opacity-0 scale-95',
            ].join(' '),
            role: variant === 'error' ? 'alert' : 'status',
        },
        icon,
        createElement(
            'div',
            { className: 'flex-1 min-w-0' },
            createElement('p', { className: 'text-sm font-medium text-gray-900 leading-snug' }, title),
            description
                ? createElement(
                    'p',
                    { className: 'mt-0.5 text-xs text-gray-500 leading-snug' },
                    description,
                )
                : null,
        ),
        createElement(
            'button',
            {
                type: 'button',
                onClick: () => toast.dismiss(t.id),
                className: 'shrink-0 -mr-1 -mt-1 rounded p-1 text-gray-300 hover:bg-gray-100 hover:text-gray-600',
                'aria-label': 'Dismiss notification',
            },
            // tiny X — use the same lucide style as the rest of the app
            createElement('span', { 'aria-hidden': true, className: 'block leading-none text-base font-light' }, '×'),
        ),
    );
}

function fire(
    variant: 'success' | 'error' | 'warning' | 'info',
    title: string,
    options?: NotifyOptions,
): string {
    const duration = options?.duration ?? (variant === 'error' ? ERROR_DURATION_MS : DEFAULT_DURATION_MS);
    return toast.custom(
        (t) => makeBody(t, variant, title, options?.description),
        {
            id: options?.id,
            duration,
            position: options?.position || DEFAULT_POSITION,
        },
    );
}

export const notify = {
    success: (title: string, options?: NotifyOptions) => fire('success', title, options),
    error:   (title: string, options?: NotifyOptions) => fire('error', title, options),
    warning: (title: string, options?: NotifyOptions) => fire('warning', title, options),
    info:    (title: string, options?: NotifyOptions) => fire('info', title, options),

    /**
     * Long-running operation. Returns the toast id; pass it back to
     * `notify.success` / `notify.error` via the `id` option to swap
     * the loading toast in-place.
     *
     *   const id = notify.loading('Saving…');
     *   try { await save(); notify.success('Saved', { id }); }
     *   catch (e) { notify.error('Save failed', { id }); }
     */
    loading: (title: string, options?: NotifyOptions): string => {
        return toast.loading(title, {
            id: options?.id,
            duration: options?.duration ?? Infinity,
            position: options?.position || DEFAULT_POSITION,
        });
    },

    /** Dismiss a specific toast by id (returned from any of the above). */
    dismiss: (id?: string) => toast.dismiss(id),
};

export default notify;
